import {
  collectSurface,
  type CalculatedAttributesRequest,
} from '@hyphengroup/cms-components/calculatedAttributes/collect';
import type { CmsProduct } from '@hyphengroup/cms-components/types/cmsProduct';
import type { VerticalType } from '@hyphengroup/cms-components/types/cmsTypes';
import type { BrandId } from '@hyphengroup/cms-components/utils/override';
import {
  getPriorityAttributeNames,
  hasPersonalLoanLeadForm,
} from '../components/ProductCard/PriorityAttribute';

/**
 * What this page asks the calculated-attributes engine for. It stands where a
 * block's collector would: the form has no dynamic zone to walk, but it
 * returns the same `CalculatedAttributesRequest`, so the executors, the store
 * and the resolver take it unchanged (CMS-1959).
 */

/** The store slot this page fills. One product, so one scope. */
export const applyCalculationScopeId = (product: CmsProduct): string =>
  `apply-v3:${product.title ?? 'unknown'}`;

/** What this form asks the engine for: its own surface, judged by the engine's core. */
export const collectApplyCalculatedAttributes = (
  product: CmsProduct | undefined,
  vertical: VerticalType | undefined,
  brand: BrandId,
): CalculatedAttributesRequest[] => {
  const productId = product?.title;
  if (!productId || !vertical) return [];
  // Elsewhere the card shows the catalogue values it always has.
  if (!hasPersonalLoanLeadForm(brand)) return [];

  const { request } = collectSurface(
    {
      moduleId: applyCalculationScopeId(product),
      vertical,
      // The names the card is configured to draw for this vertical, rather
      // than the attribute shells the product happens to carry: the request is
      // about what the surface intends to show.
      groups: [
        {
          productIds: [productId],
          attributeNames: getPriorityAttributeNames(vertical, brand),
        },
      ],
      // No loan fields and no trip: the form authors neither.
    },
    { vertical },
  );
  return request ? [request] : [];
};
